import { Label } from "components/label"
import { CollapsibleSection } from "../../../../../../components/collapsibleSection"
import Input from "components/input"
import Select from "components/select"
import { SwitchButton } from "components/switchButton"
import { EventType, ICreateOrgForm } from "interfaces"
import {
  Controller,
  UseFormReturn,
} from "react-hook-form";
import { EVENT_TYPE_OPTIONS, NATIONALITY_OPTIONS, PHONE_CODE } from "utills/constants"
import { HttpError } from "@refinedev/core"
import Dropzone from "components/dropzone"
import CONFIG from "app-config/config.json";

export const OrgInformationSection = (props: UseFormReturn<ICreateOrgForm, HttpError, ICreateOrgForm>) => {
  const { register, formState: { errors }, control, watch } = props;

  return (
    <CollapsibleSection label="Organization Information">
      <div className="divide-y divide-gray-100">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
            <div className="sm:col-span-6">
              <Label 
                required
                id="organization_name" 
                name="Organization name"  
                hint="The name will be used and displayed on the Organizer List page and the Organizer Details page on the ActiUp website."
              />
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 lg:gap-x-6">
                <div>
                  <p className="w-32 text-sm font-medium text-center max-w-full mb-[-4px] border border-magnolia-600 rounded-t-md bg-magnolia-500 px-4 py-2">
                    Vietnam
                  </p>
                  <Input 
                    id="organization_name_vi"
                    type="text"
                    placeholder="Organization name (vi)"
                    className="bg-white"
                    maxLength={100}
                    error={errors["organization"]?.name_vi}
                    {...register("organization.name_vi")}
                  />
                  <div className="flex justify-between flex-row-reverse">
                    <p className="text-xs leading-6">{(watch("organization.name_vi") ?? "").length}/100</p>
                    {errors["organization"]?.name_vi ? ( 
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["organization"]?.name_vi.message}
                        </span>
                      </p> )
                    : null}
                  </div>
                </div>
                <div>
                  <p className="w-32 text-sm font-medium text-center max-w-full mb-[-4px] border border-magnolia-600 rounded-t-md bg-magnolia-500 px-4 py-2">
                    English
                  </p>
                  <Input 
                    id="organization_name_en"
                    type="text"
                    placeholder="Organization name (en)"
                    className="bg-white"
                    maxLength={100}
                    error={errors["organization"]?.name_en}
                    {...register("organization.name_en")}
                  />
                  <div className="flex justify-between flex-row-reverse">
                    <p className="text-xs leading-6">{(watch("organization.name_en") ?? "").length}/100</p>
                    {errors["organization"]?.name_en ? ( 
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["organization"]?.name_en.message}
                        </span>
                      </p> )
                    : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 lg:gap-x-6">
                <Controller
                  control={control}
                  name="organization.is_active"
                  defaultValue={true}
                  render={({ field: { onChange, value = true, ref } }) => (
                    <div>
                      <Label 
                        required
                        id="status" 
                        name="Status"  
                      />
                      <SwitchButton checked={value} onChange={onChange} />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="organization.event_type"
                  defaultValue={[]}
                  render={({ field: { onChange, value = [], ref } }) => (
                    <div>
                      <Label 
                        required
                        id="organization.event_type" 
                        name="Event type"  
                      />
                      <Select 
                        isMulti
                        options={EVENT_TYPE_OPTIONS} 
                        id="event_type"
                        value={EVENT_TYPE_OPTIONS.filter(item => value.includes(item.value))}
                        onChange={(selected: {label: string; value: EventType}[]) => onChange(selected.map(item => `${item.value}`).sort().reverse())}
                        error={errors["organization"]?.event_type}
                      />
                      {errors["organization"]?.event_type ? ( 
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["organization"].event_type.message}
                          </span>
                        </p> )
                      : null}
                    </div>
                  )}
                />
              </div>        
            </div>
            <div className="sm:col-span-3">
              <Label 
                required
                id="country" 
                name="Country"  
              />
              <Controller
                control={control}
                name="organization.country"
                defaultValue="Vietnam"
                render={({ field: { onChange, value = "", ref } }) => (
                  <Select 
                    inputRef={ref}
                    options={NATIONALITY_OPTIONS} 
                    id="country"
                    placeholder="Select Country"
                    value={NATIONALITY_OPTIONS.find(c => c.label === value)}
                    onChange={(selected: {label: string; value: string;}) => onChange(selected?.label)}
                    error={errors["organization"]?.country}
                  />
                )}
              />

              {errors["organization"]?.country ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["organization"]?.country.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-3">
              <Label 
                required
                id="workspace_domain" 
                name="Workspace domain"
                hint="Creating a subdomain for accessing the dedicated workspace tool for the organization."
              />
              <div className="flex">
                <Input
                  id="workspace_domain"
                  className="rounded-r-none"
                  placeholder="Subdomain name"
                  maxLength={50}
                  error={errors["organization"]?.workspace_domain}
                  {...register("organization.workspace_domain")}  
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                  .workspace.actiup.net
                </span>
              </div>
              <div className="flex justify-between flex-row-reverse">
                <p className="text-xs leading-6">{(watch("organization.workspace_domain") ?? "").length}/50</p>
                {errors["organization"]?.workspace_domain ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["organization"]?.workspace_domain.message}
                    </span>
                  </p> )
                : null}
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                required
                id="landing_page" 
                name="Landing page domain"
                hint="Creating a subdomain for accessing the dedicated landing page for the organization."
              />
              <div className="flex">
                <Input
                  id="landing_page"
                  className="rounded-r-none"
                  placeholder="Subdomain name"
                  maxLength={50}
                  error={errors["organization"]?.landing_page}
                  {...register("organization.landing_page")}
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                  .actiup.net
                </span>
              </div>
              <div className="flex justify-between flex-row-reverse">
                <p className="text-xs leading-6">{(watch("organization.landing_page") ?? "").length}/50</p>
                {errors["organization"]?.landing_page ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["organization"]?.landing_page.message}
                    </span>
                  </p> )
                : null}
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                required
                id="organization_slug" 
                name="Organization slug"
                hint="It is used to create a 'address bar' for the Organization Detail page on the actiup.net website. Format: text-text-text"
              />
              <div className="flex">
                <p className="shrink-0 flex max-w-[50%] items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm" style={{wordBreak: "break-word"}}>
                  <span className="line-clamp-2 w-full">{CONFIG.DOMAIN}/organizers/</span>
                </p>
                <Input
                  id="organization_slug"
                  className="rounded-l-none"
                  placeholder="Slug name"
                  maxLength={100}
                  error={errors["organization"]?.organization_slug}
                  {...register("organization.organization_slug")}
                />
              </div>
              <div className="flex justify-between flex-row-reverse">
                <p className="text-xs leading-6">{(watch("organization.organization_slug") ?? "").length}/100</p>
                {errors["organization"]?.organization_slug ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["organization"]?.organization_slug.message}
                    </span>
                  </p> )
                : null}
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="organization.contact_phone" 
                name="Contact phone"
              />
              <div className="flex">
                <Controller
                  control={control}
                  name="organization.contact_phone_area_code"
                  defaultValue="84"
                  render={({ field: { onChange, value = "", ref } }) => {
                    const areaPhoneCodeArr = PHONE_CODE.map(item => ({label: item.label, value: item.code}));
                    return (
                      <Select 
                        ref={ref}
                        className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                        options={areaPhoneCodeArr} 
                        id="organization.contact_phone_area_code"
                        value={areaPhoneCodeArr.find(c => c.value === value)}
                        onChange={(selected: {label: string; value: string;}) => onChange(selected?.value)}
                        error={errors["organization"]?.contact_phone_area_code}
                      />
                    )
                  }}
                />
                <Input
                  id="organization.contact_phone"
                  type="phone"
                  className={`${errors["organization"]?.contact_phone ? "border-l-[1px] focus:!ml-0" : "border-l-0"} rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                  placeholder="Phone number"
                  error={errors["organization"]?.contact_phone}
                  {...register("organization.contact_phone")}
                />
              </div>
              {errors["organization"]?.contact_phone ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["organization"]?.contact_phone.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-6">
              <Label 
                id="contact_email" 
                name="Contact email"  
              />
              <div className="grid gap-3 sm:grid-cols-2 lg:gap-4">
                <div>
                  <Input 
                    id="contact_email"
                    type="text"
                    placeholder="Contact Email"
                    error={errors["organization"]?.contact_email}
                    {...register("organization.contact_email")}  
                  />
                  {errors["organization"]?.contact_email ? ( 
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["organization"]?.contact_email.message}
                      </span>
                    </p> )
                  : null}
                </div>
              </div>
            </div>
            <div className="sm:col-span-6">
              <div className="grid gap-3 sm:grid-cols-3 lg:gap-4">
                <div>
                  <Label 
                    id="organization.cover_banner" 
                    name="Cover banner (1400x300)"
                    hint="The image will be used and displayed as the cover banner in the Organization Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
                  />
                  <Controller
                    control={control}
                    name="organization.cover_banner"
                    defaultValue=""
                    render={({ field: { onChange, value = "" } }) => {
                      return (
                        <Dropzone
                          id="organization.cover_banner"
                          validator={{
                            width: 1400,
                            height: 300
                          }}
                          onChange={(imageUrl: string) => onChange(imageUrl)}
                          value={value}
                        />
                      )
                    }}
                  />
                  {errors["organization"]?.cover_banner ? ( 
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["organization"]?.cover_banner.message}
                      </span>
                    </p> )
                  : null}
                </div>
                <div>
                  <Label 
                    id="organization.organization_banner" 
                    name="Organization banner (1440x600)"
                    hint="The image will be used and displayed on the Organizer Information card in the Organizer List page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
                  />
                  <Controller
                    control={control}
                    name="organization.organization_banner"
                    defaultValue=""
                    render={({ field: { onChange, value = "" } }) => {
                      return (
                        <Dropzone
                          id="organization.organization_banner"
                          validator={{
                            width: 1440,
                            height: 600
                          }}
                          onChange={(imageUrl: string) => onChange(imageUrl)}
                          value={value}
                        />
                      )
                    }}
                  />
                  {errors["organization"]?.organization_banner ? ( 
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["organization"]?.organization_banner.message}
                      </span>
                    </p> )
                  : null}
                </div>
                <div>
                  <Label 
                    id="organization.organization_logo" 
                    name="Organization logo (600x600)"
                    hint="The image will be used and displayed as a round image in the Organizer Details page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
                  />
                  <Controller
                    control={control}
                    name="organization.organization_logo"
                    defaultValue=""
                    render={({ field: { onChange, value = "" } }) => {
                      return (
                        <Dropzone
                          id="organization.organization_logo"
                          validator={{
                            width: 600,
                            height: 600
                          }}
                          onChange={(imageUrl: string) => onChange(imageUrl)}
                          value={value}
                        />
                      )
                    }}
                  />
                  {errors["organization"]?.organization_logo ? ( 
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["organization"]?.organization_logo.message}
                      </span>
                    </p> )
                  : null}
                </div>
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="organization.rectangular_promotion_banner" 
                name="Rectangular promotion banner (950x300)"
                hint="The image will be used and displayed as the promotion banner in the Organization Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
              />
              <Controller
                control={control}
                name="organization.rectangular_promotion_banner"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="organization.rectangular_promotion_banner"
                      validator={{
                        width: 950,
                        height: 300
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["organization"]?.rectangular_promotion_banner ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["organization"]?.rectangular_promotion_banner.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="organization.square_promotion_banner" 
                name="Square promotion banner (300x300)"
                hint="The image will be used and displayed as the promotion banner in the Organization Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
              />
              <Controller
                control={control}
                name="organization.square_promotion_banner"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="organization.square_promotion_banner"
                      validator={{
                        width: 300,
                        height: 300
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["organization"]?.square_promotion_banner ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["organization"]?.square_promotion_banner.message}
                  </span>
                </p> )
              : null}
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>

  )
}